import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { RegulatoryStatusOption } from "generated-graphql/graphql";
import { useRenderTracker } from "hooks/useRenderTracker";
import React, { memo, useCallback, useState } from "react";
import { metricsLogger } from "utils/metricsLogger";

// Generator status options - copied exactly from index.tsx
const GENERATOR_STATUS_OPTIONS: {
  label: string;
  value: RegulatoryStatusOption;
}[] = [
  { label: "LQG", value: RegulatoryStatusOption.LargeQuantityGenerator },
  { label: "SQG", value: RegulatoryStatusOption.SmallQuantityGenerator },
  { label: "VSQG", value: RegulatoryStatusOption.VerySmallQuantityGenerator },
  { label: "None", value: RegulatoryStatusOption.None },
];

const ALL_GENERATOR_STATUSES = [
  RegulatoryStatusOption.LargeQuantityGenerator,
  RegulatoryStatusOption.SmallQuantityGenerator,
  RegulatoryStatusOption.VerySmallQuantityGenerator,
  RegulatoryStatusOption.None,
];

export interface GeneratorStatusFilterProps {
  /** Current committed values from omnisearch */
  committedGeneratorStatuses: RegulatoryStatusOption[];
  /** Called when dropdown closes with new values */
  onCommit: (statuses: RegulatoryStatusOption[]) => void;
}

/**
 * Memoized Generator Status filter component that manages its own open state.
 * This prevents parent re-renders when the dropdown opens/closes.
 */
export const GeneratorStatusFilter = memo(function GeneratorStatusFilter({
  committedGeneratorStatuses,
  onCommit,
}: GeneratorStatusFilterProps) {
  useRenderTracker(
    "GeneratorStatusFilter",
    { committedGeneratorStatuses },
    { logger: metricsLogger, logToConsole: false }
  );
  // === LOCAL STATE - changes here don't re-render parent ===
  const [open, setOpen] = useState(false);
  const [intermediateStatuses, setIntermediateStatuses] = useState<RegulatoryStatusOption[]>([]);

  // Display intermediate when open, committed when closed
  const selectedGeneratorStatuses = open ? intermediateStatuses : committedGeneratorStatuses;

  // Check if at default (all selected)
  const isAtDefault = selectedGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length;

  // Initialize intermediate state when dropdown opens
  const handleOpen = useCallback(() => {
    setIntermediateStatuses([...committedGeneratorStatuses]);
    setOpen(true);
  }, [committedGeneratorStatuses]);

  // Commit changes when dropdown closes
  const handleClose = useCallback(() => {
    setOpen(false);
    onCommit(intermediateStatuses);
  }, [intermediateStatuses, onCommit]);

  // Handle selection changes - only updates intermediate state
  const handleChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value as string[];
    // MUI Select fires onChange when clicking buttons inside - ignore if null/undefined values present
    if (raw.some((v) => v === null || v === undefined)) {
      return;
    }
    const values = raw.filter(
      (v) =>
        v !== "select-all" &&
        v !== null &&
        v !== undefined &&
        ALL_GENERATOR_STATUSES.includes(v as RegulatoryStatusOption)
    ) as RegulatoryStatusOption[];
    setIntermediateStatuses(values);
  }, []);

  // Toggle all generator statuses handler - toggles between all selected and none selected
  const handleToggleAll = useCallback(() => {
    if (intermediateStatuses.length === ALL_GENERATOR_STATUSES.length) {
      setIntermediateStatuses([]);
    } else {
      setIntermediateStatuses([...ALL_GENERATOR_STATUSES]);
    }
  }, [intermediateStatuses.length]);

  // Reset generator statuses to default (all selected)
  const handleReset = useCallback(() => {
    setIntermediateStatuses([...ALL_GENERATOR_STATUSES]);
  }, []);

  return (
    <FormControl variant="standard" size="small" sx={{ width: 140 }}>
      <InputLabel id="generator-status-filter-label">Gen Status</InputLabel>
      <Select
        labelId="generator-status-filter-label"
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple
        value={selectedGeneratorStatuses}
        onChange={handleChange}
        label="Gen Status"
        variant="standard"
        inputProps={{
          autoComplete: "off",
          "data-lpignore": "true",
          "data-form-type": "other",
          "data-1p-ignore": "true",
        }}
        sx={{
          "& .MuiSelect-select": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
        renderValue={(selected) => {
          if (selected.length === ALL_GENERATOR_STATUSES.length) {
            return `All (${selected.length})`;
          }
          return selected
            .map((s) => GENERATOR_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s)
            .join(", ");
        }}
        MenuProps={{
          // Prevent menu from affecting body scroll, which triggers DataGrid's ResizeObserver
          disableScrollLock: true,
          PaperProps: {
            style: { minWidth: 280 },
          },
        }}
      >
        <Box
          sx={{
            pl: 2,
            pr: 1,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: 240,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length}
                indeterminate={
                  selectedGeneratorStatuses.length > 0 &&
                  selectedGeneratorStatuses.length < ALL_GENERATOR_STATUSES.length
                }
                onChange={handleToggleAll}
              />
            }
            label="Show/Hide All"
            sx={{ margin: 0 }}
          />
          {!isAtDefault && <Button onClick={handleReset}>Reset</Button>}
        </Box>
        {GENERATOR_STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedGeneratorStatuses.includes(option.value)} />
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});
